import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { QUEUE_HANDOFF_MS } from '@config/queueConfig'
import { BoltIcon, CalendarIcon, ChevronDownIcon, ClockIcon, RefreshIcon } from './queueIcons'

/**
 * Screen 3 — the report page's Run control: a SPLIT button.
 *
 * The main action stays "Run report" (that is why you opened this report), and
 * the dropdown makes the other paths explicit — chosen over a plain "Queue
 * report" button precisely so people notice there are scheduling options beyond
 * run-now.
 *
 * FOUR items, not three: "Run in the background now" is separate from "Queue for
 * later" because they answer different problems — background-now is for a user
 * who already knows the report is huge and shouldn't have to wait 20 seconds to
 * be told.
 *
 * The two scheduling items were gated behind an In Progress badge until the
 * server-side scheduler landed (spec 058); they are live now and both open the
 * queue modal with the matching "when" preselected.
 */
const RunQueueSplitButton = ({ onRun, onRunBackground, onSchedule, isLoading, disabled, queueEnabled = true, label, neutral = false }) => {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  const toggleRef = useRef(null)
  // Index-keyed refs rather than a DOM query, so arrow-key navigation never
  // reads the DOM during render.
  const itemRefs = useRef([])
  const menuId = useId()

  // close() only sets state — focus restoration happens in the effect below.
  // Keeping the ref read out of this closure matters: the menu items hold
  // onSelect closures that call close(), and an item array whose closures reach
  // a ref reads as a ref access during render when the array is iterated.
  const close = useCallback(() => setOpen(false), [])

  // Return focus to the trigger whenever the menu closes, so keyboard users
  // aren't dumped at the top of the document.
  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (wasOpenRef.current && !open) toggleRef.current?.focus()
    wasOpenRef.current = open
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') close() }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey) }
  }, [open, close])

  // The first ENABLED item, not index 0: "Run report now" is disabled while a run
  // is in flight (spec 131), and a disabled item renders without a ref.
  useEffect(() => { if (open) itemRefs.current.find(Boolean)?.focus() }, [open])

  const seconds = Math.round(QUEUE_HANDOFF_MS / 1000)

  // `menuIndex` is assigned explicitly rather than derived with
  // filter().indexOf() — deriving it means iterating an array whose onSelect
  // closures reach a ref, which reads as a ref access during render.
  const items = [
    {
      key: 'now',
      menuIndex: 0,
      icon: <BoltIcon className="w-4 h-4 shrink-0 text-cb-accent-text" />,
      title: 'Run report now',
      body: `Results on this page. If it passes ${seconds} seconds we move it to your queue.`,
      onSelect: () => { close(); onRun?.() },
      highlight: true,
      /*
       * Spec 131 (decision 4): parity with the main half, which is
       * `disabled={disabled || isLoading}`. This item used to stay live while
       * "Running…" showed beside it, and was the second-run path measured on QA
       * (Fee Receipts 22 and Billable Hours 190: a second POST /reports/jobs
       * ~500 ms after the first). The two queue items below stay enabled on
       * purpose — they are what a user reaches for mid-run — and the pages'
       * latest-run gate keeps their result from being overwritten by the run
       * they superseded.
       */
      disabled: !!(disabled || isLoading),
    },
    {
      key: 'background',
      menuIndex: 1,
      icon: <RefreshIcon className="w-4 h-4 shrink-0 text-gray-600" />,
      title: 'Run in the background now',
      body: 'Start it now, keep working, pick it up from the queue.',
      onSelect: () => { close(); onRunBackground?.() },
      disabled: !queueEnabled,
    },
    {
      key: 'later',
      menuIndex: 2,
      icon: <ClockIcon className="w-4 h-4 shrink-0 text-gray-600" />,
      title: 'Queue for later…',
      body: 'Pick a date and time.',
      onSelect: () => { close(); onSchedule?.('once') },
      disabled: !queueEnabled,
    },
    {
      key: 'recurring',
      menuIndex: 3,
      icon: <CalendarIcon className="w-4 h-4 shrink-0 text-cb-teal-dark" />,
      title: 'Set up a recurring schedule…',
      body: 'Daily, weekly, monthly — with the date range rolling forward.',
      onSelect: () => { close(); onSchedule?.('repeat') },
      disabled: !queueEnabled,
    },
  ]

  const onItemKeyDown = (e) => {
    const els = itemRefs.current.filter(Boolean)
    if (!els.length) return
    // The position among the ENABLED items, not the item's menuIndex: with "Run
    // report now" disabled mid-run (spec 131) the two differ by one, and stepping
    // by menuIndex would skip "Queue for later…".
    const pos = Math.max(0, els.indexOf(e.currentTarget))
    if (e.key === 'ArrowDown') { e.preventDefault(); els[(pos + 1) % els.length]?.focus() }
    if (e.key === 'ArrowUp') { e.preventDefault(); els[(pos - 1 + els.length) % els.length]?.focus() }
  }

  /* spec 092: once a result exists this becomes a neutral "Refresh". Colour and
     label only — geometry, welded halves and the four menu items are identical in
     both states, so the bar cannot reflow at the moment a report finishes.
     (2026-08-26: that last clause was a SAFETY CLAIM WITH NO TEST, and it was
     false when written — .btn-cb-primary had no border while .btn-cb-neutral had
     a 1px one, so under border-box the two states measured 36px and 38px and the
     bar grew 2px the instant a report finished. Fixed in index.css by giving
     .btn-cb-primary a same-colour 1px border. What actually holds the geometry
     is identical padding, identical font metrics, and a 1px border on BOTH
     states — change the border width on one and you must change the other.)

     The drop shadow now agrees with the fill instead of contradicting it. The
     original rationale here read "the drop shadow goes with the orange: a white
     button casting the brand's blue CTA shadow reads as a rendering bug" — that
     INVERTED on 2026-08-26 when B1 made the primary CTA blue (#1d4ed8,
     --color-rc-chip-text, white on it 6.70:1). shadow-cb is rgba(0,94,178,.12),
     so it is now a blue lift under a blue button, which is the ordinary thing.
     The conditional survives for the OTHER half of the reason, which never
     depended on the hue: the neutral state is a plain outlined white control and
     a coloured lift under it still reads as a stray render. */
  const tone = neutral ? 'btn-cb-neutral' : 'btn-cb-primary'

  return (
    <div className="relative inline-flex" ref={rootRef}>
      {/* B2 — rounded-rc-control (6px), was rounded-cb (17px). This wrapper is
          what SHAPES the drop shadow, so it is not cosmetic bookkeeping: the two
          welded halves moved to 6px with the rest of the button system, and
          leaving this at 17px would have drawn a 17px shadow around a 6px
          control. --radius-cb itself is untouched — it stays a brand token for
          its five remaining non-button consumers. */}
      <div className={`inline-flex rounded-rc-control ${neutral ? '' : 'shadow-cb'}`}>
        <button
          type="button"
          data-pendo="report-run"
          onClick={onRun}
          disabled={disabled || isLoading}
          className={`${tone} btn-cb-split-l`}
        >
          {label || (isLoading ? 'Running…' : 'Run report')}
        </button>
        <button
          ref={toggleRef}
          type="button"
          aria-label="More run options"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          onKeyDown={(e) => { if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true) } }}
          className={`${tone} btn-cb-split-r`}
        >
          <ChevronDownIcon />
        </button>
      </div>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="Run options"
          className="absolute right-0 top-[calc(100%+7px)] z-50 w-[308px] rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg"
        >
          {items.map((item) => {
            const idx = item.menuIndex ?? -1
            const content = (
              <>
                {item.icon}
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5">
                    <b className="text-[12.8px] font-semibold text-cb-navy">{item.title}</b>
                  </span>
                  <span className="mt-0.5 block text-[11.5px] leading-snug text-gray-500">{item.body}</span>
                </span>
              </>
            )
            if (item.disabled) {
              return (
                <div
                  key={item.key}
                  role="menuitem"
                  aria-disabled="true"
                  className="flex cursor-not-allowed items-start gap-2.5 rounded-lg px-2.5 py-2.5 opacity-60"
                >
                  {content}
                </div>
              )
            }
            return (
              <button
                key={item.key}
                ref={(el) => { itemRefs.current[idx] = el }}
                type="button"
                role="menuitem"
                onClick={item.onSelect}
                onKeyDown={onItemKeyDown}
                className={`flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2.5 text-left transition-colors hover:bg-gray-50 ${item.highlight ? 'bg-cb-light' : ''}`}
              >
                {content}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default RunQueueSplitButton
